import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FileText, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { formatDisplayCalendarDay, formatDisplayDateRange } from '@/lib/date-format';
import { PlanningItemForm } from '@/features/planning/components/PlanningItemForm';
import { PlanningWorklogForm } from '@/features/planning/components/PlanningWorklogForm';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useDeletePlanningItem,
  useMovePlanningItem,
  usePlanningWeek,
  useUpdatePlanningWeekLiveStatus,
  useUpdatePlanningWeekNote,
} from '@/hooks/use-planning';
import type { PlanningItem } from '@/features/planning/types';
import { Textarea } from '@/components/ui/textarea';

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function toDateKey(value: string): string {
  return value.slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDate(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDisplayProjectName(item: PlanningItem): string {
  if (item.project) return item.project.name;
  if (item.customWorkGroup) return item.customWorkGroup.name;
  return 'Unknown';
}

function getDisplayListName(item: PlanningItem): string | null {
  if (item.list) return item.list.name;
  if (item.customActivity) return item.customActivity.name;
  return null;
}

function getProjectColor(item: PlanningItem): string | undefined {
  return item.project?.color;
}

function isCustomWorkGroup(item: PlanningItem): boolean {
  return !!item.customWorkGroupId || !!item.customWorkGroup;
}

function isCustomActivity(item: PlanningItem): boolean {
  return !!item.customActivityId || !!item.customActivity;
}

function getContrastTextColor(hexColor: string): '#111827' | '#ffffff' {
  const normalized = hexColor.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return '#ffffff';
  }

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.65 ? '#111827' : '#ffffff';
}

function getShiftPillClass(shift: PlanningItem['shift']) {
  if (shift === 'morning') {
    return 'border-transparent bg-sky-500 text-white';
  }
  if (shift === 'afternoon') {
    return 'border-transparent bg-indigo-500 text-white';
  }
  return 'border-transparent bg-muted text-muted-foreground';
}

function getRolePillClass(role: PlanningItem['role']) {
  if (role === 'primary') {
    return 'border-transparent bg-cyan-600 text-white';
  }
  if (role === 'cover') {
    return 'border-transparent bg-violet-600 text-white';
  }
  return 'border-transparent bg-muted text-muted-foreground';
}

function getRowKey(item: PlanningItem): string {
  const agentPart = item.agentId;
  const projectPart = item.projectId ?? item.customWorkGroupId ?? 'none';
  const activityPart = item.listId ?? item.customActivityId ?? 'none';
  return `${agentPart}|${projectPart}|${activityPart}`;
}

function normalizeAgentLabel(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

interface CombinationRow {
  key: string;
  agentId: string;
  projectName: string;
  activityName: string | null;
}

interface AgentGroup {
  agentId: string;
  sortName: string;
  primaryName: string;
  secondaryName: string | null;
  rows: CombinationRow[];
}

interface DraggablePlanningItemProps {
  item: PlanningItem;
  canMove: boolean;
  canEditItem: boolean;
  canDeleteItem: boolean;
  canSubmitWorklog: boolean;
  isDeleting: boolean;
  onEdit: (item: PlanningItem) => void;
  onDelete: (item: PlanningItem) => void;
  onWorklog: (item: PlanningItem) => void;
}

function DraggablePlanningItem({
  item,
  canMove,
  canEditItem,
  canDeleteItem,
  canSubmitWorklog,
  isDeleting,
  onEdit,
  onDelete,
  onWorklog,
}: DraggablePlanningItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: !canMove,
    data: {
      type: 'planning-item',
      item,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dragProps = canMove ? { ...attributes, ...listeners } : {};

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`rounded-md border bg-card p-2.5 text-xs shadow-sm transition-all ${
        canMove
          ? `cursor-grab border-foreground/25 hover:border-foreground/40 hover:shadow-md ${
              isDragging ? 'cursor-grabbing border-primary/60 shadow-lg ring-2 ring-primary/20' : ''
            }`
          : 'border-foreground/25'
      }`}
      {...dragProps}
    >
      <div className="min-w-0 space-y-2">
        {/* Project/Work Group then List/Activity (stacked vertically) */}
        <div className="min-w-0 space-y-1">
          <div className="min-w-0">
            {item.description ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80">
                    <Badge
                      className="max-w-full rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                      style={
                        getProjectColor(item)
                          ? {
                              backgroundColor: getProjectColor(item),
                              color: getContrastTextColor(getProjectColor(item)!),
                              borderColor: getProjectColor(item),
                            }
                          : isCustomWorkGroup(item)
                            ? { backgroundColor: '#7c3aed', color: '#ffffff', borderColor: '#7c3aed' }
                            : undefined
                      }
                      variant={getProjectColor(item) || isCustomWorkGroup(item) ? undefined : 'default'}
                      title={getDisplayProjectName(item)}
                    >
                      <span className="block max-w-[10rem] truncate">{getDisplayProjectName(item)}</span>
                    </Badge>
                    <FileText className="h-3 w-3 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3" align="start">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-foreground">{getDisplayProjectName(item)}</p>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{item.description}</p>
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Badge
                className="max-w-full rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                style={
                  getProjectColor(item)
                    ? {
                        backgroundColor: getProjectColor(item),
                        color: getContrastTextColor(getProjectColor(item)!),
                        borderColor: getProjectColor(item),
                      }
                    : isCustomWorkGroup(item)
                      ? { backgroundColor: '#7c3aed', color: '#ffffff', borderColor: '#7c3aed' }
                      : undefined
                }
                variant={getProjectColor(item) || isCustomWorkGroup(item) ? undefined : 'default'}
                title={getDisplayProjectName(item)}
              >
                <span className="block max-w-[11rem] truncate">{getDisplayProjectName(item)}</span>
              </Badge>
            )}
          </div>
          {getDisplayListName(item) && (
            <div className="min-w-0">
              {item.description ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80">
                      <Badge
                        variant="secondary"
                        className={`max-w-full rounded-full border-transparent px-1.5 py-0.5 text-[10px] font-semibold text-white ${
                          isCustomActivity(item) ? 'bg-teal-600' : 'bg-slate-600'
                        }`}
                        title={getDisplayListName(item) ?? ''}
                      >
                        <span className="block max-w-[10rem] truncate">{getDisplayListName(item)}</span>
                      </Badge>
                      <FileText className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="start">
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-foreground">{getDisplayListName(item)}</p>
                      <p className="text-xs text-muted-foreground whitespace-pre-wrap">{item.description}</p>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <Badge
                  variant="secondary"
                  className={`max-w-full rounded-full border-transparent px-1.5 py-0.5 text-[10px] font-semibold text-white ${
                    isCustomActivity(item) ? 'bg-teal-600' : 'bg-slate-600'
                  }`}
                  title={getDisplayListName(item) ?? ''}
                >
                  <span className="block max-w-[11rem] truncate">{getDisplayListName(item)}</span>
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Shift & Role pills */}
        {(item.shift || item.role) && (
          <div className="flex flex-wrap gap-1">
            {item.shift && (
              <Badge
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${getShiftPillClass(item.shift)}`}
              >
                {item.shift}
              </Badge>
            )}
            {item.role && (
              <Badge
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${getRolePillClass(item.role)}`}
              >
                {item.role}
              </Badge>
            )}
          </div>
        )}

        {/* Worklog status badge */}
        <div>
          <Badge
            variant={item.workLog ? 'success' : 'warning'}
            className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
          >
            {item.workLog ? 'Completed' : 'Missing'}
          </Badge>
        </div>

        {/* Action buttons */}
        {(canSubmitWorklog || canEditItem || canDeleteItem) && (
          <div
            className="flex flex-wrap justify-end gap-1 border-t border-border/40 pt-2"
            onPointerDownCapture={(event) => event.stopPropagation()}
          >
            {canSubmitWorklog && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className={`h-6 border px-2 text-[10px] font-semibold shadow-sm ${
                  item.workLog
                    ? 'border-transparent bg-emerald-600 text-white hover:bg-emerald-500'
                    : 'border-transparent bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
                onClick={() => onWorklog(item)}
              >
                {item.workLog ? 'Edit worklog' : 'Submit worklog'}
              </Button>
            )}
            {canEditItem && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 gap-1 px-2 text-[10px]"
                onClick={() => onEdit(item)}
              >
                <Pencil className="h-2.5 w-2.5" />
                Edit
              </Button>
            )}
            {canDeleteItem && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="h-6 gap-1 px-2 text-[10px]"
                disabled={isDeleting}
                onClick={() => onDelete(item)}
              >
                {isDeleting ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <Trash2 className="h-2.5 w-2.5" />
                )}
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function DragOverlayContent({ item }: { item: PlanningItem }) {
  const displayListName = getDisplayListName(item);
  return (
    <div className="rounded-md border-2 border-primary bg-card p-2.5 text-xs shadow-xl ring-4 ring-primary/20">
      <div className="space-y-1.5">
        {/* Project/Work Group then List/Activity */}
        <div className="min-w-0 space-y-1">
          <div className="min-w-0">
            <Badge
              className="max-w-full rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
              style={
                getProjectColor(item)
                  ? {
                      backgroundColor: getProjectColor(item),
                      color: getContrastTextColor(getProjectColor(item)!),
                      borderColor: getProjectColor(item),
                    }
                  : isCustomWorkGroup(item)
                    ? { backgroundColor: '#7c3aed', color: '#ffffff', borderColor: '#7c3aed' }
                    : undefined
              }
              variant={getProjectColor(item) || isCustomWorkGroup(item) ? undefined : 'default'}
              title={getDisplayProjectName(item)}
            >
              <span className="block max-w-[11rem] truncate">{getDisplayProjectName(item)}</span>
            </Badge>
          </div>
          {displayListName && (
            <div className="min-w-0">
              <Badge
                variant="secondary"
                className={`max-w-full rounded-full border-transparent px-1.5 py-0.5 text-[10px] font-semibold text-white ${
                  isCustomActivity(item) ? 'bg-teal-600' : 'bg-slate-600'
                }`}
                title={displayListName}
              >
                <span className="block max-w-[11rem] truncate">{displayListName}</span>
              </Badge>
            </div>
          )}
        </div>

        {/* Shift & Role pills */}
        {(item.shift || item.role) && (
          <div className="flex flex-wrap gap-1">
            {item.shift && (
              <Badge
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${getShiftPillClass(item.shift)}`}
              >
                {item.shift}
              </Badge>
            )}
            {item.role && (
              <Badge
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${getRolePillClass(item.role)}`}
              >
                {item.role}
              </Badge>
            )}
          </div>
        )}

        {/* Worklog status badge */}
        <Badge
          variant={item.workLog ? 'success' : 'warning'}
          className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
        >
          {item.workLog ? 'Completed' : 'Missing'}
        </Badge>
      </div>
    </div>
  );
}

interface DroppableCellProps {
  cellId: string;
  canDrop: boolean;
  isActive: boolean;
  children: React.ReactNode;
}

function DroppableCell({ cellId, canDrop, isActive, children }: DroppableCellProps) {
  const { setNodeRef, isOver } = useSortable({
    id: cellId,
    disabled: !canDrop,
    data: {
      type: 'cell',
    },
  });

  return (
    <td
      ref={setNodeRef}
      className={`border border-border bg-background p-2 align-top transition-colors ${
        canDrop && isActive ? 'ring-1 ring-inset ring-primary/30' : ''
      } ${isOver && canDrop ? 'bg-primary/10' : ''}`}
    >
      {children}
    </td>
  );
}

export function PlanningWeekPage() {
  const { weekId } = useParams<{ weekId: string }>();
  const { data: week, isLoading, isError } = usePlanningWeek(weekId);
  const { hasCapability } = usePermissions();
  const deletePlanningItem = useDeletePlanningItem();
  const movePlanningItem = useMovePlanningItem();
  const updateWeekNote = useUpdatePlanningWeekNote();
  const updateWeekLiveStatus = useUpdatePlanningWeekLiveStatus();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PlanningItem | null>(null);
  const [worklogItem, setWorklogItem] = useState<PlanningItem | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<PlanningItem | null>(null);
  const [isEditingWeekNote, setIsEditingWeekNote] = useState(false);
  const [weekNoteDraft, setWeekNoteDraft] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const weekItems = useMemo(() => week?.items ?? [], [week?.items]);
  const weekDays = useMemo(() => {
    if (!week?.startDate) return [];
    const startDate = new Date(`${week.startDate.slice(0, 10)}T00:00:00`);
    return DAY_LABELS.map((label, index) => {
      const date = addDays(startDate, index);
      const dateKey = formatDate(date);
      return { label, dateKey };
    });
  }, [week?.startDate]);

  const weekDateKeys = useMemo(() => weekDays.map((day) => day.dateKey), [weekDays]);

  const { agentGroups, itemsByRowAndDay, itemsById } = useMemo(() => {
    // Build stable agent + combination row groups from all items in the week
    const combinationRowsMap = new Map<string, CombinationRow>();
    const agentLabelsMap = new Map<
      string,
      {
        sortName: string;
        primaryName: string;
        secondaryName: string | null;
      }
    >();
    const nextItemsById = new Map<string, PlanningItem>();

    for (const item of weekItems) {
      nextItemsById.set(item.id, item);
      const rowKey = getRowKey(item);
      const publicName = item.agent?.publicName?.trim() ?? '';
      const displayName = item.agent?.displayName?.trim() ?? '';
      const primaryName = publicName || displayName || 'Unknown agent';
      const publicNameNormalized = normalizeAgentLabel(publicName);
      const displayNameNormalized = normalizeAgentLabel(displayName);
      const secondaryName =
        publicName && displayName && publicNameNormalized !== displayNameNormalized
          ? displayName
          : null;
      const sortName = `${primaryName} ${secondaryName ?? ''}`.trim();
      agentLabelsMap.set(item.agentId, { sortName, primaryName, secondaryName });
      if (!combinationRowsMap.has(rowKey)) {
        combinationRowsMap.set(rowKey, {
          key: rowKey,
          agentId: item.agentId,
          projectName: getDisplayProjectName(item),
          activityName: getDisplayListName(item),
        });
      }
    }

    const rowsByAgent = new Map<string, CombinationRow[]>();
    for (const row of combinationRowsMap.values()) {
      const existing = rowsByAgent.get(row.agentId);
      if (existing) {
        existing.push(row);
      } else {
        rowsByAgent.set(row.agentId, [row]);
      }
    }

    const nextAgentGroups: AgentGroup[] = Array.from(rowsByAgent.entries())
      .map(([agentId, rows]) => ({
        agentId,
        sortName: agentLabelsMap.get(agentId)?.sortName ?? 'Unknown agent',
        primaryName: agentLabelsMap.get(agentId)?.primaryName ?? 'Unknown agent',
        secondaryName: agentLabelsMap.get(agentId)?.secondaryName ?? null,
        rows: [...rows].sort((a, b) => {
          const projectCompare = a.projectName.toLowerCase().localeCompare(b.projectName.toLowerCase());
          if (projectCompare !== 0) return projectCompare;
          const actA = a.activityName?.toLowerCase() ?? '';
          const actB = b.activityName?.toLowerCase() ?? '';
          return actA.localeCompare(actB);
        }),
      }))
      .sort((a, b) => a.sortName.toLowerCase().localeCompare(b.sortName.toLowerCase()));

    // Index items by rowKey + dayKey for O(1) lookup
    const nextItemsByRowAndDay = weekItems.reduce<Record<string, Record<string, PlanningItem[]>>>(
      (acc, item) => {
        const rowKey = getRowKey(item);
        const dayKey = toDateKey(item.date);
        if (!acc[rowKey]) acc[rowKey] = {};
        if (!acc[rowKey][dayKey]) acc[rowKey][dayKey] = [];
        acc[rowKey][dayKey].push(item);
        return acc;
      },
      {},
    );

    // Sort items within each cell by position, then by id for stability
    for (const rowKey of Object.keys(nextItemsByRowAndDay)) {
      for (const dayKey of Object.keys(nextItemsByRowAndDay[rowKey])) {
        nextItemsByRowAndDay[rowKey][dayKey].sort((a, b) => {
          const positionDiff = a.position - b.position;
          if (positionDiff !== 0) return positionDiff;
          return a.id.localeCompare(b.id);
        });
      }
    }

    return {
      agentGroups: nextAgentGroups,
      itemsByRowAndDay: nextItemsByRowAndDay,
      itemsById: nextItemsById,
    };
  }, [weekItems]);

  const canCreateItem = hasCapability('planning.item.create');
  const canEditItem = hasCapability('planning.item.edit');
  const canDeleteItem = hasCapability('planning.item.delete');
  const canSubmitWorklog = hasCapability('planning.worklog.submit');
  const canMoveItem = hasCapability('planning.item.move');
  const canManageWeekNote = hasCapability('planning.description.manage');
  const canPublishWeek = hasCapability('planning.week.publish');
  const currentWeekId = week?.id;
  const normalizedWeekNote = week?.note ?? '';
  const isNoteDirty = weekNoteDraft.trim() !== normalizedWeekNote;

  useEffect(() => {
    if (!isEditingWeekNote) {
      setWeekNoteDraft(week?.note ?? '');
    }
  }, [isEditingWeekNote, week?.note]);

  const handleSaveWeekNote = useCallback(async () => {
    if (!currentWeekId || !canManageWeekNote) return;
    try {
      await updateWeekNote.mutateAsync({
        weekId: currentWeekId,
        data: { note: weekNoteDraft },
      });
      toast({ title: 'Week note updated' });
      setIsEditingWeekNote(false);
    } catch (error: unknown) {
      toast({
        title: 'Failed to update week note',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  }, [canManageWeekNote, currentWeekId, updateWeekNote, weekNoteDraft]);

  const handleToggleLiveStatus = useCallback(async (newLiveStatus: boolean) => {
    if (!currentWeekId || !canPublishWeek) return;
    try {
      await updateWeekLiveStatus.mutateAsync({
        weekId: currentWeekId,
        data: { isLive: newLiveStatus },
      });
      toast({ title: newLiveStatus ? 'Week is now live' : 'Week set to draft' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to update week status',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  }, [canPublishWeek, currentWeekId, updateWeekLiveStatus]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const draggedItem = itemsById.get(active.id as string);
    if (draggedItem) {
      setActiveItem(draggedItem);
    }
  }, [itemsById]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveItem(null);

    const { active, over } = event;
    if (!over) return;

    const draggedItemId = active.id as string;
    const draggedItem = itemsById.get(draggedItemId);
    if (!draggedItem) return;

    const overId = over.id as string;

    let targetDateKey: string;
    let targetPosition: number;
    let targetRowKey: string;

    if (overId.startsWith('cell-')) {
      // Cell ID format: cell-|rowKey|dateKey where rowKey is projectId|activityId
      const parts = overId.split('|');
      // cell|agentId|projectPart|activityPart|dateKey
      parts.shift();
      targetDateKey = parts.pop() ?? '';
      targetRowKey = parts.join('|');
      if (!targetDateKey || !targetRowKey) return;
      const targetCellItems = itemsByRowAndDay[targetRowKey]?.[targetDateKey] ?? [];
      targetPosition = targetCellItems.length;
    } else {
      const overItem = itemsById.get(overId);
      if (!overItem) return;

      targetRowKey = getRowKey(overItem);
      targetDateKey = toDateKey(overItem.date);

      const targetCellItems = itemsByRowAndDay[targetRowKey]?.[targetDateKey] ?? [];
      const overIndex = targetCellItems.findIndex((item) => item.id === overId);

      const sourceRowKey = getRowKey(draggedItem);
      const sourceDateKey = toDateKey(draggedItem.date);
      const sourceAgentId = sourceRowKey.split('|')[0];
      const targetAgentId = targetRowKey.split('|')[0];
      if (sourceAgentId !== targetAgentId) return;
      const sameCell = sourceRowKey === targetRowKey && sourceDateKey === targetDateKey;

      if (sameCell) {
        const draggedIndex = targetCellItems.findIndex((item) => item.id === draggedItemId);
        if (draggedIndex === overIndex) return;
        targetPosition = overIndex;
      } else {
        targetPosition = overIndex;
      }
    }

    const sourceRowKey = getRowKey(draggedItem);
    const sourceDateKey = toDateKey(draggedItem.date);
    const sameCell = sourceRowKey === targetRowKey && sourceDateKey === targetDateKey;
    const sourceItems = itemsByRowAndDay[sourceRowKey]?.[sourceDateKey] ?? [];
    const currentIndex = sourceItems.findIndex((item) => item.id === draggedItemId);
    if (sameCell && currentIndex === targetPosition) return;

    // For the move API, we still need to use the item's agentId since that's what the backend expects
    // Moving within the same row (same project/activity) just changes date/position
    try {
      if (!currentWeekId) return;
      await movePlanningItem.mutateAsync({
        id: draggedItemId,
        weekId: currentWeekId,
        data: {
          agentId: draggedItem.agentId,
          date: targetDateKey,
          position: targetPosition,
        },
      });
    } catch (error: unknown) {
      toast({
        title: 'Failed to move planning item',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  }, [currentWeekId, itemsById, itemsByRowAndDay, movePlanningItem]);

  const handleDragCancel = () => {
    setActiveItem(null);
  };

  const handleDelete = useCallback(async (item: PlanningItem) => {
    if (!canDeleteItem) return;

    const confirmed = window.confirm('Delete this planning item?');
    if (!confirmed) return;

    try {
      if (!currentWeekId) return;
      setDeletingItemId(item.id);
      await deletePlanningItem.mutateAsync({
        id: item.id,
        weekId: currentWeekId,
      });
      toast({ title: 'Planning item deleted' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to delete planning item',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    } finally {
      setDeletingItemId(null);
    }
  }, [canDeleteItem, currentWeekId, deletePlanningItem]);

  if (!weekId) {
    return (
      <Card>
        <CardContent className="py-8 text-sm text-destructive">Invalid week ID.</CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-sm text-muted-foreground">Loading week details...</CardContent>
      </Card>
    );
  }

  if (isError || !week) {
    return (
      <Card>
        <CardContent className="py-8 text-sm text-destructive">
          Failed to load planning week.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-2xl font-bold">Planning Week</h1>
            {canPublishWeek ? (
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="week-live-toggle"
                  className={`text-sm font-medium ${
                    week.isLive ? 'text-emerald-600' : 'text-red-500'
                  }`}
                >
                  {week.isLive ? 'Live' : 'Draft'}
                </Label>
                <Switch
                  id="week-live-toggle"
                  checked={week.isLive}
                  onCheckedChange={(checked) => void handleToggleLiveStatus(checked)}
                  disabled={updateWeekLiveStatus.isPending}
                />
              </div>
            ) : (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  week.isLive
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                <span
                  className={`inline-block h-2 w-2 rounded-full ${
                    week.isLive ? 'bg-emerald-500' : 'bg-red-500'
                  }`}
                />
                {week.isLive ? 'Live' : 'Draft'}
              </span>
            )}
          </div>
          <p className="text-muted-foreground">
            {formatDisplayDateRange(week.startDate, week.endDate)}
          </p>
        </div>
        {canCreateItem && (
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add planning item
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Weekly Planning View</CardTitle>
        </CardHeader>
        <CardContent>
          {agentGroups.length > 0 ? (
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="min-w-44 border border-border bg-muted px-3 py-2 text-left font-semibold text-foreground">
                        Agent
                      </th>
                      {weekDays.map((day) => (
                        <th key={day.dateKey} className="min-w-32 border border-border bg-muted px-3 py-2 text-left">
                          <p className="font-semibold text-foreground">{day.label}</p>
                          <p className="text-xs font-normal text-muted-foreground">
                            {formatDisplayCalendarDay(day.dateKey)}
                          </p>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {agentGroups.map((agentGroup) =>
                      agentGroup.rows.map((row, rowIndex) => (
                        <tr key={row.key}>
                          {rowIndex === 0 && (
                            <td
                              rowSpan={agentGroup.rows.length}
                              className="border border-border bg-muted/20 px-3 py-2 align-top"
                            >
                              <p className="text-sm font-semibold text-foreground">{agentGroup.primaryName}</p>
                              {agentGroup.secondaryName && (
                                <p className="text-xs text-muted-foreground">{agentGroup.secondaryName}</p>
                              )}
                            </td>
                          )}
                          {weekDays.map((day) => {
                            const items = itemsByRowAndDay[row.key]?.[day.dateKey] ?? [];
                            const cellId = `cell|${row.key}|${day.dateKey}`;

                            return (
                              <DroppableCell
                                key={`${row.key}-${day.dateKey}`}
                                cellId={cellId}
                                canDrop={canMoveItem}
                                isActive={!!activeItem}
                              >
                                {items.length === 0 ? (
                                  <p className="text-xs text-muted-foreground">&nbsp;</p>
                                ) : (
                                  <SortableContext
                                    items={items.map((item) => item.id)}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    <ul className="space-y-2">
                                      {items.map((item) => (
                                        <DraggablePlanningItem
                                          key={item.id}
                                          item={item}
                                          canMove={canMoveItem}
                                          canEditItem={canEditItem}
                                          canDeleteItem={canDeleteItem}
                                          canSubmitWorklog={canSubmitWorklog}
                                          isDeleting={deletePlanningItem.isPending && deletingItemId === item.id}
                                          onEdit={setEditingItem}
                                          onDelete={(i) => void handleDelete(i)}
                                          onWorklog={setWorklogItem}
                                        />
                                      ))}
                                    </ul>
                                  </SortableContext>
                                )}
                              </DroppableCell>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <DragOverlay>
                {activeItem && <DragOverlayContent item={activeItem} />}
              </DragOverlay>
            </DndContext>
          ) : (
            <p className="text-sm text-muted-foreground">
              This week has no visible planning items yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Weekly note</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isEditingWeekNote ? (
            <>
              <Textarea
                value={weekNoteDraft}
                onChange={(event) => setWeekNoteDraft(event.target.value)}
                maxLength={2000}
                placeholder="Add internal context for this planning week..."
                rows={4}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => void handleSaveWeekNote()}
                  disabled={!isNoteDirty || updateWeekNote.isPending}
                >
                  {updateWeekNote.isPending ? 'Saving...' : 'Save note'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setWeekNoteDraft(week.note ?? '');
                    setIsEditingWeekNote(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {week.note?.trim() ? week.note : 'No planning note.'}
              </p>
              {canManageWeekNote && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setWeekNoteDraft(week.note ?? '');
                    setIsEditingWeekNote(true);
                  }}
                >
                  {week.note?.trim() ? 'Edit note' : 'Add note'}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Button variant="outline" asChild>
        <Link to="/planning">Back to weeks</Link>
      </Button>

      {canCreateItem && createDialogOpen && (
        <PlanningItemForm
          mode="create"
          weekId={week.id}
          weekDates={weekDateKeys}
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
        />
      )}

      {canEditItem && editingItem && (
        <PlanningItemForm
          mode="edit"
          item={editingItem}
          weekId={week.id}
          weekDates={weekDateKeys}
          open={!!editingItem}
          onOpenChange={(open) => {
            if (!open) setEditingItem(null);
          }}
        />
      )}

      {canSubmitWorklog && worklogItem && (
        <PlanningWorklogForm
          item={worklogItem}
          open={!!worklogItem}
          onOpenChange={(open) => {
            if (!open) setWorklogItem(null);
          }}
        />
      )}
    </div>
  );
}
